import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  Users,
  Hash,
  Sparkles,
  Briefcase,
  User,
  // UserCircle,
} from "lucide-react";
import { RoutesLink } from "@/utility/RouteLinks";
import type { NavigateFunction } from "react-router-dom";

export interface EmployeeNode {
  id: string;
  name: string;
  role: string;
  department: string;
  employeeCode: string;
  reports?: EmployeeNode[];
}


const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};


const colors = [
  "rgb(14, 165, 233)",
  "rgb(139, 92, 246)",
  "rgb(236, 72, 153)",
  "rgb(245, 158, 11)",
  "rgb(16, 185, 129)",
  "rgb(239, 68, 68)",
  "rgb(99, 102, 241)",
  "rgb(20, 184, 166)",
];

const getColorForName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Helper to check if a node corresponds to the currently logged in user
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCurrentUser = (node: EmployeeNode, userDetails: any): boolean => {
  if (!userDetails) return false;
  if (node.id && userDetails.id && String(node.id) === String(userDetails.id)) return true;
  if (
    node.employeeCode &&
    node.employeeCode !== "—" &&
    userDetails.employeeCode &&
    String(node.employeeCode) === String(userDetails.employeeCode)
  ) {
    return true;
  }
  if (
    node.name &&
    userDetails.name &&
    node.name.toLowerCase().trim() === userDetails.name.toLowerCase().trim()
  ) {
    return true;
  }
  return false;
};

// Recursively count total employees
export const countTotalEmployees = (node: EmployeeNode): number => {
  if (node.id === "synthetic-root") {
    return (node.reports || []).reduce((acc, curr) => acc + countTotalEmployees(curr), 0);
  }
  return 1 + (node.reports || []).reduce((acc, curr) => acc + countTotalEmployees(curr), 0);
};

// Recursively count unique departments
export const countDepartments = (node: EmployeeNode, depts = new Set<string>()): number => {
  if (node.department && node.department !== "—" && node.id !== "synthetic-root") {
    depts.add(node.department);
  }
  (node.reports || []).forEach((r) => countDepartments(r, depts));
  return depts.size;
};

export const OrgTreeNode: React.FC<{
  node: EmployeeNode;
  isRoot?: boolean;
  depth?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userDetails?: any;
  defaultExpandDepth?: number;
  searchQuery?: string;
  navigate: NavigateFunction;
}> = ({
  node,
  depth = 0,
  userDetails,
  defaultExpandDepth = 2,
  searchQuery = "",
  navigate
}) => {
    const hasReports = node.reports && node.reports.length > 0;
    const totalReports = hasReports ? node.reports!.length : 0;

    // Auto-expand if within default depth or if search matches children
    const isSearchMatch = Boolean(
      searchQuery &&
      (node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.employeeCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.department.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const [isExpanded, setIsExpanded] = useState(depth < defaultExpandDepth || isSearchMatch);

    useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsExpanded(depth < defaultExpandDepth || isSearchMatch);
    }, [defaultExpandDepth, depth, isSearchMatch]);

    const isMe = isCurrentUser(node, userDetails);

    return (
      <div className="relative">
        {/* Connector line from parent if depth > 0 */}
        {depth > 0 && (
          <div className="absolute -left-[18px] top-5 w-4 h-[2px] bg-slate-300/80 transition-colors" />
        )}

        {/* Node Card */}
        <div
          onClick={() => hasReports && setIsExpanded(!isExpanded)}
          className={`group relative flex flex-col sm:flex-row sm:items-center justify-between py-2.5 px-3.5 my-1.5 rounded-xl border transition-all duration-300 gap-2.5 ${hasReports ? "cursor-pointer" : ""
            } ${isMe
              ? "bg-gradient-to-r from-primary/15 via-primary/5 to-white border-primary/60 shadow-md shadow-primary/5 ring-1 ring-primary/40 scale-[1.005]"
              : isSearchMatch
                ? "bg-amber-50/80 border-amber-400 shadow-xs ring-1 ring-amber-300"
                : "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-sm hover:bg-slate-50/60"
            }`}
        >
          {/* Left section: Expand button + Avatar + Info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Expand/Collapse Chevron */}
            <button
              type="button"
              className={`w-5 h-5 rounded-lg flex items-center justify-center transition-all shrink-0 ${!hasReports
                ? "invisible"
                : isExpanded
                  ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 transition-transform duration-200" />
              )}
            </button>

            {/* Avatar with Status indicator */}
            <div className="relative shrink-0">
              <div
                className="inline-flex items-center justify-center rounded-xl text-white font-bold shadow-xs transition-transform duration-300 group-hover:scale-105"
                style={{
                  width: "36px",
                  height: "36px",
                  fontSize: "13px",
                  backgroundColor: getColorForName(node.name),
                }}
              >
                {getInitials(node.name)}
              </div>
              {/* Online/Active status dot */}
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white shadow-2xs ${isMe ? "bg-emerald-500 animate-pulse" : "bg-emerald-400"
                  }`}
              />
            </div>

            {/* Name & Role & Dept */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`font-semibold text-sm tracking-tight truncate ${isMe ? "text-primary font-bold" : "text-slate-800"
                    }`}
                >
                  {node.department}
                </span>
                {isMe && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary text-primary-foreground shadow-2xs animate-pulse">
                    <Sparkles className="w-3 h-3" /> You
                  </span>
                )}
                {isSearchMatch && !isMe && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                    Match
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                  <Briefcase className="w-3 h-3 text-slate-400" /> {node.role}
                  {/* <User className="w-3 h-3 text-slate-400" /> {node.name} */}
                  {/* <UserCircle className="w-3 h-3 text-slate-400" /> {node.name} */}
                  {/* <IdCard className="w-3 h-3 text-slate-400" /> {node.name} */}
                </span>
                <span className="text-slate-300">•</span>
                {/* <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-medium border border-slate-200/60">
      {node.name}
    </span> */}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    const targetId = node.id;
                    if (targetId && targetId !== "synthetic-root") {
                      navigate(`${RoutesLink.employeeDetail}/${targetId}`);
                    }
                  }}
                  title="View Employee Details"
                  className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200/80 text-slate-600 hover:text-primary px-1.5 py-0.5 rounded text-[10px] font-medium border border-slate-200/60 whitespace-nowrap cursor-pointer transition-colors"
                >
                  <User className="w-3 h-3 text-slate-400 shrink-0" />
                  {node.name}
                </span>
              </div>
            </div>
          </div>

          {/* Right section: Emp Code + Reports Pill */}
          <div className="flex items-center justify-end gap-2 shrink-0 sm:ml-2 border-t sm:border-t-0 pt-1.5 sm:pt-0 border-slate-100 w-full sm:w-auto">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200/80 text-[11px] font-mono font-medium text-slate-600">
              <Hash className="w-3 h-3 text-slate-400" />
              {node.employeeCode}
            </span>

            {hasReports ? (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-all ${isExpanded
                  ? "bg-primary/10 text-primary border border-primary/20"
                  : "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200/80"
                  }`}
              >
                <Users className="w-3 h-3" />
                {totalReports} {totalReports === 1 ? "Report" : "Reports"}
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium text-slate-400 bg-slate-50/80 border border-slate-100">
                Individual
              </span>
            )}
          </div>
        </div>

        {/* Children Container with left guideline */}
        {hasReports && isExpanded && (
          <div className="relative pl-5 ml-3.5 border-l-2 border-slate-300/80 space-y-1 pt-0.5 pb-1.5 transition-all">
            {node.reports!.map((report) => (
              <OrgTreeNode
                key={report.id}
                node={report}
                isRoot={false}
                depth={depth + 1}
                userDetails={userDetails}
                defaultExpandDepth={defaultExpandDepth}
                searchQuery={searchQuery}
                navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
